import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../translations';
import { CheckIcon, DownloadIcon, PrinterIcon, QrIcon } from '../../ui/icons';
import { qrDataUrl, qrPngDoPobrania, qrSvgDoPobrania } from '../labels/codeImages';
import PrintFrame, { printFrame } from '../print/PrintFrame';

// Link do rezerwacji i kod QR (F.4). Wypożyczalnia bez własnej strony WWW wysyła klientom link
// do strony widgetu albo drukuje kod QR (lada, ulotka, drzwi). Adres buduje
// utils/widgetHost.js#linkRezerwacji; tu tylko kopiowanie, pobieranie i druk.

const Karta = styled.section`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
`;

const Naglowek = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;

  svg { color: var(--primary); flex-shrink: 0; }
`;

const Opis = styled.p`
  color: var(--text-muted);
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const Notka = styled.p`
  margin: 0 0 16px 0;
  padding: 10px 14px;
  border-radius: var(--r-md);
  border: 1px solid var(--warning-border);
  background: var(--warning-bg);
  color: var(--warning);
  font-size: 0.85rem;
  line-height: 1.5;
`;

const Uklad = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Kolumna = styled.div`
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Etykieta = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
`;

const PoleLinku = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--input-bg);
  color: var(--text);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
`;

const Przyciski = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Przycisk = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: var(--r-md);
  border: 1px solid ${(p) => (p.$glowny ? 'transparent' : 'var(--input-border)')};
  background: ${(p) => (p.$glowny ? 'var(--primary)' : 'var(--card-bg)')};
  color: ${(p) => (p.$glowny ? 'var(--primary-contrast)' : 'var(--text)')};
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const OtworzLink = styled.a`
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--primary);
  align-self: flex-start;
`;

// Kod na białym tle z marginesem także w trybie ciemnym: aparat telefonu potrzebuje kontrastu.
const RamkaKodu = styled.div`
  flex: 0 0 auto;
  width: 168px;
  height: 168px;
  padding: 12px;
  background: #fff;
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  display: flex;
  align-items: center;
  justify-content: center;

  img { width: 100%; height: 100%; display: block; }
`;

const Komunikat = styled.p`
  margin: 0;
  font-size: var(--fs-sm);
  color: ${(p) => (p.$blad ? 'var(--danger)' : 'var(--text-muted)')};
`;

// Kartka do druku: kolory wprost, bo w dokumencie ramki nie ma zmiennych motywu (PrintFrame).
const Kartka = styled.div`
  font-family: Arial, 'Liberation Sans', sans-serif;
  color: #111;
  text-align: center;
  padding-top: 20mm;

  h1 { font-size: 26pt; margin: 0 0 6mm; }
  p { font-size: 14pt; margin: 0 0 10mm; }
  img { width: 110mm; height: 110mm; display: block; margin: 0 auto 10mm; }
  .link { font-family: 'Courier New', monospace; font-size: 9pt; color: #444; word-break: break-all; }
`;

function pobierz(href, nazwa) {
  const a = document.createElement('a');
  a.href = href;
  a.download = nazwa;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function BookingLinkQr({ link, isDemo, orgName }) {
  const { t } = useTranslation();
  const [kod, setKod] = useState('');
  const [stan, setStan] = useState(null); // null | 'skopiowano' | 'blad'
  const ramkaDruku = useRef(null);

  useEffect(() => {
    let aktualne = true;
    setKod('');
    if (link) {
      qrDataUrl(link).then((url) => { if (aktualne) setKod(url); }).catch(() => {});
    }
    return () => { aktualne = false; };
  }, [link]);

  const kopiuj = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setStan('skopiowano');
      setTimeout(() => setStan(null), 2000);
    } catch (_) {
      // Bez fałszywego „Skopiowano”: link stoi w polu obok, więc da się go zaznaczyć ręcznie.
      setStan('blad');
    }
  };

  const pobierzPng = async () => { pobierz(await qrPngDoPobrania(link), `${t('widgetMedia.bookingLink.fileName')}.png`); };
  const pobierzSvg = async () => { pobierz(await qrSvgDoPobrania(link), `${t('widgetMedia.bookingLink.fileName')}.svg`); };

  const brak = !link;

  return (
    <Karta data-cy="booking-link">
      <Naglowek>
        <QrIcon size={20} />
        {t('widgetMedia.bookingLink.title')}
      </Naglowek>
      <Opis>{t('widgetMedia.bookingLink.subtitle')}</Opis>
      {isDemo && <Notka data-cy="booking-link-demo-note">{t('widgetMedia.bookingLink.demoNote')}</Notka>}
      {brak ? (
        <Komunikat data-cy="booking-link-missing">{t('widgetMedia.bookingLink.missing')}</Komunikat>
      ) : (
        <Uklad>
          <Kolumna>
            <Etykieta htmlFor="booking-link-field">{t('widgetMedia.bookingLink.fieldLabel')}</Etykieta>
            <PoleLinku
              id="booking-link-field"
              value={link}
              readOnly
              onFocus={(e) => e.target.select()}
              data-cy="booking-link-field"
            />
            <Przyciski>
              <Przycisk type="button" $glowny onClick={kopiuj} data-cy="booking-link-copy">
                {stan === 'skopiowano' ? <><CheckIcon size={16} />{t('widgetMedia.bookingLink.copied')}</> : t('widgetMedia.bookingLink.copy')}
              </Przycisk>
              <Przycisk type="button" onClick={pobierzPng} data-cy="booking-link-png">
                <DownloadIcon size={16} />{t('widgetMedia.bookingLink.downloadPng')}
              </Przycisk>
              <Przycisk type="button" onClick={pobierzSvg} data-cy="booking-link-svg">
                <DownloadIcon size={16} />{t('widgetMedia.bookingLink.downloadSvg')}
              </Przycisk>
              <Przycisk type="button" onClick={() => printFrame(ramkaDruku.current)} disabled={!kod} data-cy="booking-link-print">
                <PrinterIcon size={16} />{t('widgetMedia.bookingLink.print')}
              </Przycisk>
            </Przyciski>
            {stan === 'blad' && <Komunikat $blad role="alert" data-cy="booking-link-copy-failed">{t('widgetMedia.bookingLink.copyFailed')}</Komunikat>}
            <OtworzLink href={link} target="_blank" rel="noopener noreferrer" data-cy="booking-link-open">
              {t('widgetMedia.bookingLink.open')}
            </OtworzLink>
          </Kolumna>
          <RamkaKodu>
            {kod && <img src={kod} alt={t('widgetMedia.bookingLink.qrAlt')} data-cy="booking-link-qr" />}
          </RamkaKodu>
        </Uklad>
      )}
      {!brak && kod && (
        <PrintFrame
          frameRef={ramkaDruku}
          title={t('widgetMedia.bookingLink.printTitle')}
          page="size: A4; margin: 15mm;"
          rootId="booking-link-print"
          dataCy="booking-link-print-frame"
        >
          <Kartka>
            {orgName ? <h1>{orgName}</h1> : null}
            <p>{t('widgetMedia.bookingLink.printHeading')}</p>
            <img src={kod} alt="" />
            <div className="link">{link}</div>
          </Kartka>
        </PrintFrame>
      )}
    </Karta>
  );
}
